import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLayoutWidth } from '@/contexts/LayoutWidthContext';
import { GraduationCap, Plus, Loader2, ChevronDown, Pencil, Trash2, AlertCircle } from 'lucide-react';
import {
    PageHeader, Button, Badge, ListHeader, FilterGroup, SearchBar, Pagination, Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
    SimpleSelect, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    AlertDialog,
} from '@/components/shared/ui';
import { cn } from '@/lib/cn';
import { ROUTES } from '@/lib/constants/routes';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminPageTitle } from '@/pages/manager/AdminManagerShell';
import { useClassListByOrganization, useDeleteClass, classKeys } from '../../data/queries/useClasses';
import type { ClassListItemDto } from '@/types/api/class';
import {
    classListItemDisplayParticipantCount,
    classListItemDisplaySessionCount,
    getClassListItemParticipationYmd,
} from '@/utils/classUtils';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import {
    buildListQueryString,
    parseListQueryString,
    type ClassManagementListState,
    saveClassManagementListQueryToStorage,
    CLASS_LIST_RETURN_STATE_KEY,
} from '@/lib/classManagementListQuery';
import { formatLocaleDateShort } from '@/lib/localeFormat';
import ClassDetail from './ClassDetail';

const ITEMS_PER_PAGE = 10;

export interface ClassManagementProps {
    organizationId?: string;
    organizationName?: string;
    suppressProductCards?: boolean;
    embedMode?: boolean;
}

export default function ClassManagement(props?: ClassManagementProps): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const {
        organizationId: propOrganizationId,
        embedMode = false,
    } = props ?? {};
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setLayoutWidth } = useLayoutWidth();
    useEffect(() => {
        if (embedMode) return;
        setLayoutWidth?.('wide');
        return () => setLayoutWidth?.('default');
    }, [setLayoutWidth, embedMode]);

    const adminTitle = useAdminPageTitle();
    const { user } = useAuth();
    const role = user?.currentAccount?.role ?? 'guest';
    const isMaster = role === 'master';
    const isAdmin = role === 'admin';
    const basePath = isMaster ? '/master' : '/admin';
    const routes = isMaster ? ROUTES.MASTER : ROUTES.ADMIN;

    // admin(role 5)은 organizationId 없으면 전체 조회, master(role 2)는 본인 기관만
    const rawOrgId = propOrganizationId ?? (isAdmin ? undefined : user?.currentAccount?.organizationId);
    const organizationId = Number(rawOrgId) || 0;

    const [searchParams, setSearchParams] = useSearchParams();
    /** embed(기관 탭)일 때는 URL·스토리지에 쓰지 않음 */
    const [embedList, setEmbedList] = useState<ClassManagementListState>({
        q: '',
        classType: 'all',
        active: 'all',
        page: 1,
    });
    const listState: ClassManagementListState = useMemo(
        () =>
            embedMode
                ? embedList
                : parseListQueryString(new URLSearchParams(searchParams.toString())),
        [embedMode, embedList, searchParams]
    );
    const searchTerm = listState.q;
    const classTypeFilter = listState.classType;
    const activeFilter = listState.active;
    const currentPage = listState.page;

    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setLocalSearchTerm(searchTerm);
    }, [searchTerm]);

    const [deleteTarget, setDeleteTarget] = useState<{ id: number; title: string } | null>(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [selectedClassId, setSelectedClassId] = useState<number | null>(null);

    const isActiveFilter = activeFilter === 'all' ? undefined : activeFilter === 'active';

    const patchListState = useCallback(
        (patch: Partial<ClassManagementListState>) => {
            if (embedMode) {
                setEmbedList((prev) => {
                    const next: ClassManagementListState = { ...prev, ...patch };
                    return {
                        q: next.q,
                        classType: next.classType,
                        active: next.active,
                        page: next.page,
                    };
                });
                return;
            }
            const current = parseListQueryString(new URLSearchParams(searchParams.toString()));
            const next: ClassManagementListState = { ...current, ...patch };
            const qs = buildListQueryString(next);
            if (!qs) {
                setSearchParams({}, { replace: true });
            } else {
                setSearchParams(new URLSearchParams(qs.replace(/^\?/, '')), { replace: true });
            }
        },
        [embedMode, searchParams, setSearchParams]
    );

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalSearchTerm(newValue);

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            patchListState({ q: newValue, page: 1 });
        }, 300);
    }, [patchListState]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (embedMode) {
            return;
        }
        saveClassManagementListQueryToStorage(basePath, buildListQueryString(listState));
    }, [listState, embedMode, basePath]);

    const navToClassFromList = useCallback(
        (item: ClassListItemDto) => {
            if (embedMode) {
                setSelectedClassId(item.classId);
                return;
            }
            const returnQuery = buildListQueryString(listState);
            const state = { [CLASS_LIST_RETURN_STATE_KEY]: returnQuery };
            if (item.isOpenClass) {
                navigate({ pathname: `${basePath}/open-class/${item.classId}` }, { state });
                return;
            }
            navigate(
                { pathname: `${basePath}/my-classes/${item.classId}`, search: '?from=class-management' },
                { state }
            );
        },
        [embedMode, basePath, listState, navigate]
    );

    // admin(role 5)은 organizationId 없으면 전체 조회, master(role 2)는 본인 기관 ID 필수
    const queryOrgId = isAdmin ? (organizationId > 0 ? organizationId : undefined) : organizationId;

    const listEnabled = isAdmin || organizationId > 0;
    const apiLanguage = useApiLanguage();

    const listFilterBase = useMemo(
        () => ({
            organizationId: queryOrgId,
            title: searchTerm.trim() || undefined,
            isActive: isActiveFilter,
        }),
        [queryOrgId, searchTerm, isActiveFilter]
    );

    // isOpenClass: API 스펙대로 true=오픈만, false=기관만, 미전송=전체
    const isOpenClassParam: boolean | undefined =
        classTypeFilter === 'open' ? true : classTypeFilter === 'organization' ? false : undefined;

    /** 서버 페이지네이션 - 모든 필터링은 API 파라미터로 처리 (API 1개만 사용) */
    const { data: listDataPaged, isLoading, error, refetch } =
        useClassListByOrganization(
            {
                ...listFilterBase,
                page: currentPage,
                pageSize: ITEMS_PER_PAGE,
                isOpenClass: isOpenClassParam,
            },
            { 
                enabled: listEnabled,
                placeholderData: (previousData) => previousData,
            }
        );

    const classList: ClassListItemDto[] = listDataPaged?.classList ?? [];
    const totalCount = listDataPaged?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

    // 클래스 타입 또는 활성 필터 변경 시 명시적으로 데이터 다시 가져오기
    const prevFiltersRef = useRef({ classType: classTypeFilter, active: activeFilter });
    useEffect(() => {
        const prev = prevFiltersRef.current;
        if (prev.classType !== classTypeFilter || prev.active !== activeFilter) {
            console.log('[ClassManagement] 필터 변경:', {
                classType: { prev: prev.classType, new: classTypeFilter },
                active: { prev: prev.active, new: activeFilter },
                isOpenClass: isOpenClassParam,
                isActive: isActiveFilter
            });
            prevFiltersRef.current = { classType: classTypeFilter, active: activeFilter };
            // refetch 함수는 안정적이므로 직접 호출 가능
            void refetch();
        }
    }, [classTypeFilter, activeFilter, isOpenClassParam, isActiveFilter, refetch]);

    const deleteMutation = useDeleteClass();

    const handlePageChange = (page: number) => {
        patchListState({ page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteClick = (item: ClassListItemDto) => {
        setDeleteTarget({ id: item.classId, title: item.title });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync(deleteTarget.id);
            setAlertMsg(t('management.deleteSuccess', { title: deleteTarget.title }));
        } catch (e) {
            setAlertMsg(e instanceof Error ? e.message : t('management.deleteFailed'));
        } finally {
            setDeleteTarget(null);
            setAlertOpen(true);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return formatLocaleDateShort(dateStr, i18n.language);
    };

    /** yyyy-MM-DD만 있을 때 UTC 자정 파싱으로 하루 밀리는 것 방지 */
    const formatYmdCell = (ymd: string) => formatDate(`${ymd}T12:00:00`);

    // embedMode에서 클래스 상세화면을 보여주는 경우
    if (embedMode && selectedClassId) {
        return (
            <ClassDetail
                classId={String(selectedClassId)}
                onClose={() => setSelectedClassId(null)}
            />
        );
    }

    return (
        <>
            {!embedMode && (
                <PageHeader
                    title={adminTitle || t('management.title')}
                    titleSize="admin"
                    actions={
                        <div className="flex gap-2">
                                {!isMaster && (
                                    <Button variant="lightdark" size="md" onClick={() => navigate(routes.OPEN_CLASS_CREATE, { state: { organizationId: organizationId || undefined } })}>
                                        <Plus size={16} className="mr-2" />
                                        {t('management.createOpenClass')}
                                    </Button>
                                )}
                                <Button variant="lightdark" size="md" onClick={() => navigate(routes.CLASS_CREATE, { state: { organizationId: organizationId || undefined } })}>
                                    <Plus size={16} className="mr-2" />
                                    {t('management.createClass')}
                                </Button>
                        </div>
                    }
                />
            )}


            {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm mb-4">
                    <AlertCircle size={16} className="shrink-0" />
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                        <span className="min-w-0 break-words">
                            {error instanceof Error ? error.message : t('management.loadFailed')}
                        </span>
                        <button type="button" onClick={() => refetch()} className="shrink-0 underline hover:no-underline">
                            {t('management.retry')}
                        </button>
                    </span>
                </div>
            )}

            {isLoading && !listDataPaged ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                    <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                    <p className="text-gray-500">{t('management.loading')}</p>
                </div>
            ) : (
                <div className="space-y-6">
                    <div>
                        <ListHeader
                            totalCount={totalCount}
                            rightContent={
                                <FilterGroup className="gap-2">
                                    <SimpleSelect
                                        value={classTypeFilter}
                                        onChange={(e) => {
                                            patchListState({
                                                classType: e.target.value as 'all' | 'open' | 'organization',
                                                page: 1,
                                            });
                                        }}
                                        wrapperClassName="w-[120px]"
                                        className="h-9"
                                    >
                                        <option value="all">{t('management.filterAll')}</option>
                                        <option value="open">{t('management.filterOpen')}</option>
                                        <option value="organization">{t('management.filterOrganization')}</option>
                                    </SimpleSelect>
                                    <SimpleSelect
                                        value={activeFilter}
                                        onChange={(e) => {
                                            patchListState({
                                                active: e.target.value as 'all' | 'active' | 'inactive',
                                                page: 1,
                                            });
                                        }}
                                        wrapperClassName="w-[106px]"
                                        className="h-9"
                                    >
                                        <option value="all">{t('management.filterStatusAll')}</option>
                                        <option value="active">{t('management.filterActive')}</option>
                                        <option value="inactive">{t('management.filterInactive')}</option>
                                    </SimpleSelect>
                                    <div className="w-64">
                                        <SearchBar
                                            placeholder={t('management.searchPlaceholder')}
                                            value={localSearchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                </FilterGroup>
                            }
                        />

                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="text-center w-[60px]">No.</TableHead>
                                    <TableHead className="text-center w-[80px]">{t('management.tableType')}</TableHead>
                                    <TableHead className="text-left">{t('management.tableClassName')}</TableHead>
                                    <TableHead className="text-center w-[70px]">{t('management.tableSessions')}</TableHead>
                                    <TableHead className="text-center w-[80px]">{t('management.tableParticipants')}</TableHead>
                                    <TableHead className="text-center w-[120px]">{t('management.tableCreatedDate')}</TableHead>
                                    <TableHead className="text-center w-[100px]">{t('management.tableCreator')}</TableHead>
                                    <TableHead className="text-center w-[200px]">{t('management.tableParticipationPeriod')}</TableHead>
                                    <TableHead className="text-center w-[80px]">{t('management.tableStatus')}</TableHead>
                                    <TableHead className="text-center w-[80px]">{t('management.tableActions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {classList.length > 0 ? (
                                    classList.map((item, idx) => {
                                        const rowNo = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
                                        return (
                                            <TableRow key={item.classId} className="hover:bg-gray-50 transition-colors text-sm">
                                                <TableCell className="text-center text-gray-500 text-xs">{rowNo}</TableCell>
                                                <TableCell className="text-center">
                                                    <Badge
                                                        variant="outline"
                                                        className={cn(
                                                            'text-xs',
                                                            item.isOpenClass
                                                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                                : 'bg-gray-100 text-gray-700 border-gray-200'
                                                        )}
                                                    >
                                                        {item.isOpenClass ? t('management.typeOpen') : t('management.typeOrg')}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        className="cursor-pointer group flex items-center gap-3"
                                                        onClick={() => navToClassFromList(item)}
                                                        onKeyDown={(e) => {
                                                            if (e.key !== 'Enter' && e.key !== ' ') return;
                                                            e.preventDefault();
                                                            navToClassFromList(item);
                                                        }}
                                                    >
                                                        <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                                                            {item.thumbnailImageUrl ? (
                                                                <img src={item.thumbnailImageUrl} alt={item.title} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <GraduationCap size={18} className="text-gray-400" />
                                                            )}
                                                        </div>
                                                        <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                                            {item.title}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-center text-gray-700 text-sm">
                                                    {classListItemDisplaySessionCount(item as ClassListItemDto & Record<string, unknown>)}
                                                </TableCell>
                                                <TableCell className="text-center text-gray-700 text-sm">
                                                    {classListItemDisplayParticipantCount(item as ClassListItemDto & Record<string, unknown>)}
                                                </TableCell>
                                                <TableCell className="text-center text-gray-500 text-xs">{formatDate(item.createdAt)}</TableCell>
                                                <TableCell className="text-center text-gray-700 text-sm">{item.organizationTitle || item.creatorName || '-'}</TableCell>
                                                <TableCell className="text-center">
                                                    {!item.isOpenClass ? (() => {
                                                        const ymd = getClassListItemParticipationYmd(
                                                            item as ClassListItemDto & Record<string, unknown>
                                                        );
                                                        if (!ymd) {
                                                            return <span className="text-xs text-gray-400">-</span>;
                                                        }
                                                        return (
                                                            <div className="text-xs text-gray-500 flex flex-col gap-0.5">
                                                                <span>{formatYmdCell(ymd.startYmd)}</span>
                                                                <span>~ {formatYmdCell(ymd.endYmd)}</span>
                                                            </div>
                                                        );
                                                    })() : (
                                                        <span className="text-xs text-gray-400">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <Badge
                                                        variant={item.isActive ? 'statusActive' : 'statusInactive'}
                                                        className="text-xs"
                                                    >
                                                        {item.isActive ? t('management.statusActive') : t('management.statusInactive')}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                                <ChevronDown size={16} />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-32">
                                                            <DropdownMenuItem onClick={() => {
                                                                // embedMode(기관 상세 페이지)에서는 쿼리 파라미터로 클래스 수정 모드 활성화
                                                                if (embedMode && propOrganizationId) {
                                                                    navigate(`${basePath}/organizations/${propOrganizationId}?section=class-management&classId=${item.classId}&mode=edit`);
                                                                } else {
                                                                    const editPath = item.isOpenClass 
                                                                        ? `${basePath}/open-class/edit/${item.classId}` 
                                                                        : `${basePath}/class/edit/${item.classId}`;
                                                                    navigate(editPath);
                                                                }
                                                            }}>
                                                                <Pencil size={14} className="mr-2" />
                                                                {t('management.actionEdit')}
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                className="text-red-600 focus:text-red-600"
                                                                onClick={() => handleDeleteClick(item)}
                                                            >
                                                                <Trash2 size={14} className="mr-2" />
                                                                {t('management.actionDelete')}
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} className="h-24 text-center text-gray-500">
                                            {t('management.noData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex justify-center pb-8">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalCount}
                            onPageChange={handlePageChange}
                            itemsPerPage={ITEMS_PER_PAGE}
                        />
                    </div>
                </div>
            )}

            {deleteTarget && (
                <AlertDialog
                    isOpen={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    message={t('management.deleteConfirm', { title: deleteTarget.title })}
                    confirmText={t('management.actionDelete')}
                    cancelText={t('management.actionCancel')}
                    onConfirm={handleDeleteConfirm}
                />
            )}
            <AlertDialog
                isOpen={alertOpen}
                onClose={() => setAlertOpen(false)}
                message={alertMsg}
            />
        </>
    );
}
